import { FC } from 'react';

import cl from 'classnames';
import { differenceInCalendarDays, isValid } from 'date-fns';

import {
  EFlightStatus,
  ETimelineType,
  ETripEvent,
  IFlightAPIEventData,
  IFlightManualEventData,
} from 'detailsv2/structure';
import i18n from 'i18n';
import { getHoursAndMinutes24 } from 'utils/date';
import { isNotDefined } from 'utils/ramda';

import { transformAndRemoveLeadingZeros, transformTime } from '../../../functions';
import { IDayTripEvent } from '../../../types';
import s from '../../index.module.css';
import { ETransferTypes, EventOptionConfig } from '../../types';
import { IFlightTime } from './types';

const FlightOption = (props: IDayTripEvent<ETripEvent.Flight> & EventOptionConfig) => {
  const { data } = props;

  if (data.fromFsService) {
    return <FlightAPI {...props} />;
  }

  return <FlightManual {...props} />;
};

export default FlightOption;

export const FlightAPI = ({
  data,
  timelineType,
  readonly,
  timeFormat,
}: IDayTripEvent<ETripEvent.Flight> & EventOptionConfig) => {
  const {
    departureAirport,
    arrivalAirport,
    arrivalTime,
    airline,
    departureTime,
    flightNumber,
    displaySettings,
    newDepartureTime,
    newArrivalTime,
    flightStatus,
    updateClientSide,
  } = data as IFlightAPIEventData;

  const getTitle = (type: ETransferTypes, showDisplaySettings: boolean = true, className?: string) => {
    const map = {
      [ETransferTypes.Departure]: {
        action: i18n.t('proposal_content.event_details.flights.departs'),
        time: departureTime,
        airport: departureAirport,
        newTime: newDepartureTime,
      },
      [ETransferTypes.Arrival]: {
        action: i18n.t('proposal_content.event_details.flights.arrives'),
        time: arrivalTime,
        airport: arrivalAirport,
        newTime: newArrivalTime,
      },
    };
    const { action, time, airport, newTime } = map[type];
    return (
      <div data-block className={className}>
        {showDisplaySettings && displaySettings && <div className={s.displaySettings}>{displaySettings}</div>}
        <FlightTime
          oldTime={time}
          newTime={newTime}
          status={flightStatus}
          hideChanges={readonly && !updateClientSide}
          timeFormat={timeFormat}
        />{' '}
        -
        {` ${i18n.t('proposal_content.event_details.flight')} ${airline.fsCode} ${flightNumber} ${action}
      ${airport.city} (${airport.name}`}
        )
      </div>
    );
  };

  switch (timelineType) {
    case ETimelineType.OneDay:
      return (
        <>
          {departureAirport && getTitle(ETransferTypes.Departure, true, s.sameDayFlightsGap)}
          {arrivalAirport && getTitle(ETransferTypes.Arrival, false)}
        </>
      );
    case ETimelineType.Start:
      return <>{departureAirport && getTitle(ETransferTypes.Departure)}</>;
    case ETimelineType.End:
      return <>{arrivalTime && getTitle(ETransferTypes.Arrival)}</>;
    default:
      return null;
  }
};

export const FlightManual = ({
  data,
  timelineType,
  timeFormat,
}: IDayTripEvent<ETripEvent.Flight> & EventOptionConfig) => {
  const { departureFrom, arrivalTo, arrivalTime, departureTime, flightNumber, displaySettings } =
    data as IFlightManualEventData;

  const getTitle = (type: ETransferTypes, showDisplaySettings: boolean = true, className?: string) => {
    const map = {
      [ETransferTypes.Departure]: {
        action: i18n.t('proposal_content.event_details.flights.departs'),
        time: departureTime,
        place: departureFrom,
      },
      [ETransferTypes.Arrival]: {
        action: i18n.t('proposal_content.event_details.flights.arrives'),
        time: arrivalTime,
        place: arrivalTo,
      },
    };
    const { action, time, place } = map[type];

    return (
      <div data-block className={className}>
        {showDisplaySettings && displaySettings && <div className={s.displaySettings}>{displaySettings}</div>}
        {time ? `${transformTime(time, timeFormat)} - ` : ''}{' '}
        {`${i18n.t('proposal_content.event_details.flight')} ${flightNumber ? flightNumber : ''} ${action} ${place}`}
      </div>
    );
  };

  switch (timelineType) {
    case ETimelineType.OneDay:
      return (
        <>
          {getTitle(ETransferTypes.Departure, true, s.sameDayFlightsGap)}
          {getTitle(ETransferTypes.Arrival, false)}
        </>
      );
    case ETimelineType.Start:
      return getTitle(ETransferTypes.Departure);
    case ETimelineType.End:
      return getTitle(ETransferTypes.Arrival);
    default:
      return null;
  }
};

const FlightTime: FC<IFlightTime> = ({ oldTime, newTime, status, hideChanges, timeFormat }) => {
  const formatTime = (val: string) => {
    if (!isValid(new Date(val))) return null;

    if (timeFormat === '24h') {
      return getHoursAndMinutes24(new Date(val), ':');
    }

    return transformAndRemoveLeadingZeros(val);
  };

  if (isNotDefined(newTime) || status !== EFlightStatus.Rescheduled || hideChanges) {
    return <>{formatTime(oldTime)}</>;
  }

  const dayDifference = differenceInCalendarDays(new Date(newTime[newTime.length - 1].date), new Date(oldTime));

  return (
    <>
      <del>{formatTime(oldTime)}</del>{' '}
      <strong className={cl(s.actionText, 'custom-flight-time-changed')}>
        {formatTime(newTime[newTime.length - 1]?.date)}
        {dayDifference ? (
          <sup style={{ fontSize: '10px' }}>
            {dayDifference > 0 ? '+' : '-'}
            {i18n.t('proposal_content.days', { count: Math.abs(dayDifference) })}
          </sup>
        ) : null}
      </strong>
    </>
  );
};
